import { z } from 'zod'

/**
 * zod 기본 검증 메시지는 영어라("Expected number, received nan" 등) 한국어 운영자에게
 * 그대로 노출된다(BIZ-28). 흔한 케이스만 우리말로 바꾸고 나머지는 zod 기본(ctx.defaultError)에 맡긴다.
 *
 * refine·커스텀 message가 붙은 이슈(예: '판매 종료일은 시작일 이후여야 합니다')는 그 문구를
 * 그대로 쓰므로 여기 오지 않는다 — 영향 없다.
 *
 * setErrorMap은 zod 인스턴스 전역에 적용된다. 모노레포에 zod가 하나뿐이라 contracts 스키마와
 * 앱이 같은 인스턴스를 공유하고, 앱 진입점에서 installKoErrorMap()을 한 번 부르면 그 앱이
 * 파싱하는 모든 스키마에 적용된다. (브라우저 번들과 API 프로세스는 런타임이 달라 서로 간섭 없음.)
 */
const koErrorMap: z.ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type: {
      // 빈 칸(undefined/null) 또는 빈 숫자칸(NaN)이 가장 흔하다.
      if (issue.received === 'undefined' || issue.received === 'null') return { message: '필수 항목입니다' }
      if (issue.received === 'nan') return { message: '숫자를 입력하세요' }
      if (issue.expected === 'integer') return { message: '정수를 입력하세요' }
      if (issue.expected === 'number') return { message: '숫자를 입력하세요' }
      return { message: ctx.defaultError }
    }
    case z.ZodIssueCode.too_small: {
      if (issue.type === 'string') {
        return { message: issue.minimum === 1 ? '필수 항목입니다' : `${issue.minimum}자 이상 입력하세요` }
      }
      if (issue.type === 'number') {
        return { message: issue.inclusive ? `${issue.minimum} 이상이어야 합니다` : `${issue.minimum}보다 커야 합니다` }
      }
      if (issue.type === 'array') return { message: `${issue.minimum}개 이상 선택하세요` }
      return { message: ctx.defaultError }
    }
    case z.ZodIssueCode.too_big: {
      if (issue.type === 'string') return { message: `${issue.maximum}자 이하로 입력하세요` }
      if (issue.type === 'number') {
        return { message: issue.inclusive ? `${issue.maximum} 이하여야 합니다` : `${issue.maximum}보다 작아야 합니다` }
      }
      if (issue.type === 'array') return { message: `${issue.maximum}개 이하로 선택하세요` }
      return { message: ctx.defaultError }
    }
    case z.ZodIssueCode.invalid_string: {
      if (issue.validation === 'email') return { message: '올바른 이메일 형식이 아닙니다' }
      if (issue.validation === 'url') return { message: '올바른 URL 형식이 아닙니다' }
      return { message: ctx.defaultError }
    }
    case z.ZodIssueCode.invalid_enum_value:
      return { message: '허용되지 않은 값입니다' }
    default:
      return { message: ctx.defaultError }
  }
}

/** 앱 진입점에서 한 번 호출. 이후 이 zod 인스턴스의 모든 파싱에 한국어 메시지가 적용된다. */
export function installKoErrorMap(): void {
  z.setErrorMap(koErrorMap)
}

export { koErrorMap }
